import cloneDeep from 'lodash-es/cloneDeep';
import clone from 'lodash-es/clone';
import isEmpty from 'lodash-es/isEmpty';
import differenceBy from 'lodash-es/differenceBy';
import merge from 'lodash-es/merge';
import unset from 'lodash-es/unset';
import indexOf from 'lodash-es/indexOf';
import clamp from 'lodash-es/clamp';
import first from 'lodash-es/first';
import keyBy from 'lodash-es/keyBy';
import map from 'lodash-es/map';
import sortBy from 'lodash-es/sortBy';
import produce, { applyPatches, produceWithPatches } from 'immer';
import { createSelector } from 'reselect';

import variables from '../../common/variables';
import { overrideImageData } from '../../common/images';
import { identifyDataObjectTree } from '../../common/objects';
import { MODE_SLIDE, MODE_SYMBOL, MODE_TEMPLATE } from '../../common/lib/modes';
import {
  migrateSlideContentObjects,
  migrateSlideContent,
} from '../../common/migrations';
import { getObjectsDiff } from '../../common/canvas';
import utils from '../../common/utils';

const UNDO_LIMIT = 500;

const undoableActions = [
  // 'SELECTION_CREATED',
  'SYMBOL_EDITION_START',
  'SYMBOL_EDITION_END',
  'TEMPLATE_EDITION_START',
  'TEMPLATE_EDITION_END',
  'SLIDES_TEMPLATES_UPDATED',
  'SLIDE_TRANSITION_UPDATED',
  'SYMBOL_EDITION_DIMENSIONS_UPDATE',
  'SELECTION_CLEARED',
  'OBJECT_SELECTED',
  'OBJECT_ADDED',
  'OBJECT_REMOVED',
  'OBJECT_MODIFIED',
  'OBJECT_LOCKED',
  'OBJECT_UNLOCKED',
  'OBJECT_UNGROUPED',
  'ALL_UNLOCKED',
  'BACKGROUND_UPDATED',
  'NEW_SLIDE',
  'PASTE_SLIDES',
  'REPLACE_IMAGE',
  'DUPLICATE_SLIDES',
  'DELETE_SLIDES',
  'REORDER',
  'SLIDES_SKIPPED',
  'SLIDES_UNSKIPPED',
  'PREVIOUS_SLIDE',
  'NEXT_SLIDE',
  'GO_TO_SLIDE',
  'THEME_NAME_CHANGED',
];

const isUndoable = action => undoableActions.includes(action.type);
const isEditingText = action => action.object && action.object.isEditing;
const isCropping = action =>
  action.object && action.object.isCropping && action.object.isCropping();

const getSlideById = (slides, id) => slides[id];
const getIndexById = (order, id) => order.indexOf(id);
const getIdByIndex = (order, index) => order[clamp(index, 0, order.length - 1)];

const getNumberById = (slides, order, id) => {
  let number;
  let index = 0;
  let skippedSlidesCount = 0;

  for (const slideId of order) {
    const slide = getSlideById(slides, slideId);
    if (slide.id === id) {
      if (slide.visible === false) {
        number = 1;
      } else {
        number = index + 1 - skippedSlidesCount;
      }
      break;
    } else {
      if (slide.visible === false) {
        skippedSlidesCount += 1;
      }
    }
    index += 1;
  }

  return number;
};

const selectMode = state => state.mode;
const selectSlides = state => state.slides;
const selectTheme = state => state.editingTheme;
const selectOrder = state => state.order;
const selectCurrentSlideId = state => state.currentSlideId;
const selectCurrentTemplateId = state => state.currentTemplateId;
const selectSymbol = state => state.symbol;
const selectTemplate = state =>
  state.editingTheme?.templates[state.currentTemplateId] || { content: {} };

export const getOrder = createSelector(
  [selectSlides, selectOrder],
  (slides, order) => utils.intersection([order, map(slides, 'id')]),
);

export const getSlidesCollection = createSelector(
  [selectSlides, selectOrder],
  (slides, order) => {
    return order.map(id => getSlideById(slides, id));
  },
);

export const getCurrentSlide = createSelector(
  [selectSlides, getOrder, selectCurrentSlideId],
  (slides, order, currentSlideId) => {
    return getSlideById(slides, currentSlideId);
  },
);

export const getCurrentTemplate = createSelector(
  [selectTheme, selectCurrentTemplateId],
  (editingTheme, currentTemplateId) => {
    return getSlideById(editingTheme.templates, currentTemplateId);
  },
);

export const getCurrentSlideId = createSelector(
  [getOrder, selectCurrentSlideId],
  (order, currentSlideId) => {
    if (getIndexById(order, currentSlideId) < 0 && order.length > 0) {
      return first(order);
    }

    return currentSlideId;
  },
);

export const getCurrentSlideNumber = createSelector(
  [selectSlides, getOrder, selectCurrentSlideId],
  (slides, order, currentSlideId) => {
    return getNumberById(slides, order, currentSlideId) || 1;
  },
);

export const getJson = createSelector(
  [selectMode, getCurrentSlide, selectSymbol, selectTemplate],
  (mode, currentSlide, symbol, template) => {
    switch (mode) {
      case MODE_SLIDE:
        return currentSlide.content;
      case MODE_SYMBOL:
        return symbol.content;
      case MODE_TEMPLATE:
        return template.content;
      default:
        return null;
    }
  },
);

const findObject = (state, object) => {
  const { objects = {} } = state.slides[state.currentSlideId].content;
  return objects[object.canvasId];
};

const removeObjectByCanvasId = (draft, canvasIds) => {
  const slideContent = draft.slides[draft.currentSlideId].content;
  canvasIds.forEach(canvasId => {
    delete slideContent.objects[canvasId];
  });
};

const applySelectionToDraft = (draft, action) => {
  if (action.object) {
    if (action.object.type === 'activeSelection') {
      draft.currentSelection = action.object._objects.map(o => o.canvasId);
    } else {
      draft.currentSelection = [action.object.canvasId];
    }
  } else {
    draft.currentSelection = [];
  }
};

const applyActionToDraft = (draft, action, fabricCanvas) => {
  switch (action.type) {
    case 'OBJECT_REMOVED': {
      removeObjectByCanvasId(draft, action.objects);
      break;
    }
    case 'OBJECT_UNGROUPED':
    case 'OBJECT_ADDED':
    case 'OBJECT_MODIFIED':
    case 'OBJECT_LOCKED':
    case 'OBJECT_UNLOCKED':
    case 'ALL_UNLOCKED': {
      /**
       * We are going to assume the fabricCanvas contains the right information.
       * If there is a new object in the fabricCanvas, this object will be added to draft.
       * The Fabric and draft objects will be compared and the differences applied properly.
       * The objects at the draft there aren't in the Fabric canvas will be removed.
       */
      const { objects = [] } = fabricCanvas.toJSON(variables.propsToSave);
      const slideContent = draft.slides[draft.currentSlideId].content;
      if (!slideContent.objects) {
        slideContent.objects = {};
      }

      /**
       * When group the elements are removed a new one is inserted.
       * When ungrouping, the opposite happens.
       */
      const removedObjects = differenceBy(
        migrateSlideContentObjects(slideContent.objects),
        objects,
        'canvasId',
      ).map(o => o.canvasId);
      removeObjectByCanvasId(draft, removedObjects);

      objects.forEach(object => {
        const objectDraft = findObject(draft, object);
        if (!objectDraft) {
          slideContent.objects[object.canvasId] = object;
          return;
        }
        const changes = getObjectsDiff(objectDraft, object);
        Object.keys(changes).forEach(key => {
          // MAKE SURE THE CHANGES ARE A NEW NULL TO ACTUALLY SAVE IN DB
          if (
            (key !== 'ludus' && !objectDraft[key] && !object[key]) ||
            ((typeof objectDraft[key] === 'object' ||
              typeof object[key] === 'object') &&
              isEmpty(objectDraft[key]) &&
              isEmpty(object[key]))
          ) {
            delete changes[key];
          }

          if (key === 'objects' && object.type === 'ludusSymbol') {
            changes[key] = object.objects || null;
          }
          if (key === 'contents' && object.type === 'ludusText') {
            changes[key] = object.contents || null;
          }
        });
        merge(objectDraft, changes);
      });
      break;
    }

    case 'BACKGROUND_UPDATED': {
      const slideContent = draft.slides[draft.currentSlideId].content;
      slideContent.background = action.background;
      break;
    }
  }
};

function decorate(state) {
  const slidesIds = map(state.slides, 'id');
  const order = utils.intersection([state.order, slidesIds]);
  const currentSlideId = state.currentSlideId;
  let currentSlideIndex = getIndexById(order, currentSlideId);
  if (currentSlideIndex < 0 && order.length > 0) {
    currentSlideIndex = getIndexById(order, currentSlideId);
  }
  const nextSlideIndex = currentSlideIndex + 1;
  const totalSlides = order.length;
  let nextSlide = null;
  let nextSlideId = null;
  if (nextSlideIndex < totalSlides) {
    nextSlideId = order[nextSlideIndex];
    nextSlide = getSlideById(state.slides, nextSlideId);
  }

  return {
    ...state,
    currentSlideIndex,
    totalSlides,
    nextSlideId,
    nextSlide,
    canGoPrevious: currentSlideIndex > 0,
    canGoNext: currentSlideIndex < totalSlides - 1,
  };
}

/**
 * This function is similar to decorate. However, it works with immer
 * draft. Meaning, we're expected to do what's normally forbidden -
 * we update the draft to the expected output value. In turn,
 * based on these changes immer produces the next immutable state
 * and patches (for both directions), which we then use for undo/redo.
 *
 * Note that when we work with immer draft, we only update the draft,
 * do not additionally return the draft/state from reducer, immer does
 * this automatically. The other decorate method works with state, so
 * it is expected it returns the new state the old way.
 *
 * In the future, we could probably get rid of those functions altogether
 * and use selectors (reselect) to infer the values added here.
 */
function mutableDecorate(draft) {
  const slidesIds = map(draft.slides, 'id');
  const order = utils.intersection([draft.order, slidesIds]);
  let currentSlideId = draft.currentSlideId;
  let currentSlideIndex = getIndexById(order, currentSlideId);
  if (currentSlideIndex < 0 && order.length > 0) {
    currentSlideId = first(order);
    currentSlideIndex = getIndexById(order, currentSlideId);
  }
  const nextSlideIndex = currentSlideIndex + 1;
  const totalSlides = order.length;
  let nextSlide = null;
  let nextSlideId = null;
  if (nextSlideIndex < totalSlides) {
    nextSlideId = order[nextSlideIndex];
    nextSlide = getSlideById(draft.slides, nextSlideId);
  }
  draft.nextSlide = nextSlide;
  draft.totalSlides = totalSlides;
  draft.currentSlideIndex = currentSlideIndex;
  draft.canGoPrevious = currentSlideIndex > 0;
  draft.canGoNext = currentSlideIndex < totalSlides - 1;
}

export default (fabricCanvas, transitions, documentSize) => {
  // In some cases (i.a. thumbnail generation), we need to compare the current slide or
  // presentation state with the previous one. As our state is not truly immutable, we
  // keep a local version number to help us compare versions quickly.
  const localSlidesVersion = () => 0;

  // Tracks changes of slide content
  const localSlideVersion = () => 0;

  const defaultState = {
    currentSlideId: null,
    currentSelection: [],
    order: [],
    slides: {
      [null]: {
        id: null,
        templateId: null,
        templateVersion: null,
        content: {
          background: '#FFF',
          objects: {},
        },
        notes: '',
        visible: null,
        transition: {
          id: transitions[0].id,
        },
        localVersion: localSlideVersion(),
      },
    },
    title: '',
    description: null,
    slug: null,
    scrollableViewer: null,
    hideControls: null,
    googleDriveId: null,
    parentModes: [],
    mode: MODE_SLIDE,
    password: null,
    symbol: {
      id: null,
      name: null,
      team: null,
      content: {
        background: '#FFF',
        objects: [],
      },
      dimensions: {
        width: null,
        height: null,
      },
    },
    template: {
      id: null,
      themeId: null,
      themeName: '',
      name: null,
      content: {
        background: '#FFF',
        objects: [],
      },
    },
    slidesObjectsRefsDataCache: {
      // [id]: {
      //   title_1: {
      //     text: 'foobar'
      //   }
      // }
    },
    canvasDimensions: documentSize,
    localSlidesVersion: 0,

    undoable: [],
    undone: [],
  };

  const getModeTitle = state =>
    ({
      [MODE_SLIDE]: state.title,
      [MODE_TEMPLATE]: state.template.name,
      [MODE_SYMBOL]: state.symbol.name,
    }[state.mode]);

  const addCurrentModeToHistory = draft => {
    draft.parentModes = [
      ...draft.parentModes,
      { mode: draft.mode, title: getModeTitle(draft) },
    ];
  };

  const updateSlidesTemplateData = (draft, data) => {
    Object.values(data).forEach(
      ({ slideId, templateId, templateVersion, ...templateData }) => {
        const slide = draft.slides[slideId];
        draft.slides[slideId] = {
          ...slide,
          templateId,
          templateVersion,
          content: templateData,
        };
      },
    );
    draft.localSlideVersion = localSlidesVersion();
  };

  const canvasReducer = (state = decorate(defaultState), action) => {
    if (action.type === 'UNDOING' || action.type === 'REDOING') {
      return {
        ...state,
        ongoing: true,
      };
    }

    if (action.type === 'UNDO') {
      if (state.undoable.length === 0) {
        return state;
      }

      try {
        const lastPatches = state.undoable[0];
        let prevSelectionIDs =
          lastPatches.inversePatches[0]?.path[0] === 'currentSelection'
            ? lastPatches.inversePatches[0].value
            : null;

        // if we modified objects, let's try to restore a previous selection
        if (
          !prevSelectionIDs &&
          (lastPatches.inversePatches[0]?.path[3] === 'objects' ||
            lastPatches.inversePatches[0]?.path[1] === 'templates')
        ) {
          const prevSelection = state.undoable.find(
            p =>
              p.patches[0]?.path[0] === 'currentSelection' &&
              p.patches[0]?.value.length,
          );
          prevSelectionIDs =
            prevSelection?.patches[0].value || state.currentSelection;
        } else if (!prevSelectionIDs) {
          prevSelectionIDs = [];
        }

        return {
          ...applyPatches(state, lastPatches.inversePatches),
          undoable: state.undoable.slice(1),
          undone: [lastPatches, ...state.undone],
          ongoing: false,
          currentSelection: prevSelectionIDs,
        };
      } catch (patchError) {
        console.info(
          'Sorry, your collaborator has prevented a clean UNDO',
          patchError,
        );
        return {
          ...state,
          undoable: state.undoable.slice(1),
          ongoing: false,
        };
      }
    }

    if (action.type === 'REDO') {
      if (state.undone.length === 0) {
        return state;
      }

      try {
        const nextPatches = state.undone[0];

        // check if the last action is about currentSelection
        // if not, we'll search for the last known currentSelection to restore
        let prevSelectionIDs =
          nextPatches.patches[0].path[0] === 'currentSelection'
            ? nextPatches.patches[0].value
            : null;
        if (!prevSelectionIDs) {
          const prevSelection = state.undoable.find(
            p =>
              p.patches[0]?.path[0] === 'currentSelection' &&
              p.patches[0]?.value.length,
          );
          prevSelectionIDs =
            prevSelection?.patches[0].value || state.currentSelection;
        }

        return {
          ...applyPatches(state, nextPatches.patches),
          currentSelection: prevSelectionIDs,
          undoable: [nextPatches, ...state.undoable],
          undone: state.undone.slice(1),
          ongoing: false,
        };
      } catch (patchError) {
        console.info(
          'Sorry, your collaborator has prevented a clean REDO',
          patchError,
        );
        return {
          ...state,
          undone: state.undone.slice(1),
          ongoing: false,
        };
      }
    }

    if (isCropping(action)) {
      return state;
    }

    if (
      [
        'TEXT_SELECTION_CHANGED',
        'TEXT_CHANGED',
        'THUMBNAIL_GENERATED',
        'THUMBNAIL_GENERATING',
        'CURSOR_MOVING',
        'OBJECT_MODIFYING',
      ].includes(action.type)
    ) {
      return state;
    }
    if (!isUndoable(action)) {
      // We don't need immer state patches when action is not undoable.
      return {
        ...produce(state, draft => {
          return internalReducer(draft, action);
        }),
      };
    }

    const [nextState, patches, inversePatches] = produceWithPatches(
      state,
      draft => {
        const actionsMutatingDraft = [
          'OBJECT_ADDED',
          'OBJECT_REMOVED',
          'OBJECT_MODIFIED',
          'OBJECT_LOCKED',
          'OBJECT_UNLOCKED',
          'OBJECT_UNGROUPED',
          'REPLACE_IMAGE',
          'ALL_UNLOCKED',
          'BACKGROUND_UPDATED',
          'NEW_SLIDE',
          'PASTE_SLIDES',
          'DUPLICATE_SLIDES',
          'DELETE_SLIDES',
          'REORDER',
          'SLIDES_SKIPPED',
          'SLIDES_UNSKIPPED',
          'PREVIOUS_SLIDE',
          'NEXT_SLIDE',
          'GO_TO_SLIDE',
        ];

        if (actionsMutatingDraft.includes(action.type)) {
          internalReducer(draft, action);
          // Do NOT return state when modifying immer draft.
          return;
        }

        return internalReducer(draft, action);
      },
    );

    const undoable =
      action === 'CANVAS_INITIALIZED' || action === 'CLEAR_HISTORY'
        ? []
        : [{ patches, inversePatches }, ...state.undoable.slice(0, UNDO_LIMIT)];

    if (['SELECTION_CLEARED', 'OBJECT_SELECTED'].includes(action.type)) {
      return {
        ...nextState,
        undoable,
      };
    }
    return {
      ...nextState,
      undoable,
      undone: [],
    };
  };

  const internalReducer = (state, action) => {
    const { currentSlideId, slides } = state;

    switch (action.type) {
      case 'CANVAS_INITIALIZED': {
        return applyCanvasInitialised(
          state,
          action,
          localSlideVersion,
          localSlidesVersion,
        );
      }

      case 'PRESENTATION_REFRESH': {
        applyPresentationRefresh(
          state,
          action,
          localSlideVersion,
          localSlidesVersion,
        );
        break;
      }

      case 'THEME_NAME_CHANGED': {
        state.editingTheme.name = action.name;
        return state;
      }
      case 'OBJECT_UNGROUPED':
      case 'OBJECT_ADDED':
      case 'OBJECT_REMOVED':
      case 'OBJECT_MODIFIED':
      case 'OBJECT_LOCKED':
      case 'OBJECT_UNLOCKED':
      case 'ALL_UNLOCKED':
      case 'BACKGROUND_UPDATED': {
        if (
          (action.type !== 'OBJECT_ADDED' && isEditingText(action)) ||
          isCropping(action)
        ) {
          return state;
        }
        switch (state.mode) {
          case MODE_SLIDE:
            applyActionToDraft(state, action, fabricCanvas);
            mutableDecorate(state);
            break;
          case MODE_SYMBOL:
            state.symbol.content = fabricCanvas.toJSON(variables.propsToSave);
            break;
          case MODE_TEMPLATE: {
            const content = fabricCanvas.toJSON(variables.propsToSave);
            state.editingTheme.templates[
              state.currentTemplateId
            ].content = content;
            // state.template.content = content;
            break;
          }
        }
        break;
      }
      case 'SELECTION_RESTORED':
      case 'SELECTION_CLEARED':
      case 'OBJECT_SELECTED': {
        applySelectionToDraft(state, action);
        break;
      }

      case 'REPLACE_IMAGE': {
        applyReplaceImage(state, action);
        break;
      }

      case 'NEW_SLIDE': {
        if (state.mode === MODE_TEMPLATE) {
          applyNewTemplate(state, action);
        } else {
          applyNewSlide(state, action, localSlideVersion, localSlidesVersion);
        }
        break;
      }

      case 'PASTE_SLIDES': {
        if (state.mode === MODE_TEMPLATE) {
          applyPasteTemplates(state, action, localSlidesVersion);
        } else {
          applyPasteSlides(state, action, localSlidesVersion);
        }
        break;
      }

      case 'DUPLICATE_SLIDES': {
        if (state.mode === MODE_TEMPLATE) {
          applyDuplicateTemplates(state, action);
        } else {
          applyDuplicateSlides(
            state,
            action,
            localSlideVersion,
            localSlidesVersion,
          );
        }
        break;
      }

      case 'PREVIOUS_SLIDE': {
        applyPreviousSlide(state, action);
        break;
      }

      case 'NEXT_SLIDE': {
        applyNextSlide(state, action);
        break;
      }

      case 'GO_TO_SLIDE': {
        applyGoToSlide(state, action);
        break;
      }

      case 'DELETE_SLIDES': {
        applyDeleteSlides(state, action, localSlidesVersion);
        break;
      }

      case 'REORDER': {
        applyReorder(state, action, localSlidesVersion);
        break;
      }

      case 'NOTES_UPDATED': {
        const slideId = action.slideId;
        const newSlides = {
          ...slides,
          [slideId]: {
            ...slides[slideId],
            notes: action.notes,
          },
        };

        return decorate({
          ...state,
          slides: newSlides,
        });
      }

      case 'SLIDES_SKIPPED': {
        applySlidesSkipped(state, action);
        break;
      }

      case 'SLIDES_UNSKIPPED': {
        applySlidesUnskipped(state, action);
        break;
      }

      case 'TITLE_CHANGED': {
        return decorate({
          ...state,
          title: action.title,
        });
      }

      case 'DESCRIPTION_CHANGED': {
        return decorate({
          ...state,
          description: action.description,
        });
      }

      case 'VIEWER_SETTINGS_CHANGED': {
        return decorate({
          ...state,
          scrollableViewer: action.scrollableViewer,
          hideControls: action.hideControls,
        });
      }

      case 'SLUG_CHANGED': {
        return decorate({
          ...state,
          slug: action.slug,
        });
      }

      case 'GOOGLE_DRIVE_ID_CHANGED': {
        return decorate({
          ...state,
          googleDriveId: action.googleDriveId,
        });
      }

      case 'SLIDES_TEMPLATES_UPDATED': {
        updateSlidesTemplateData(state, action.data);
        break;
      }

      case 'PASSWORD_CHANGED': {
        return decorate({
          ...state,
          password: action.password,
        });
      }

      case 'SLIDE_TRANSITION_UPDATED': {
        state.slides[currentSlideId].transition = {
          id: action.transition.id,
          params: action.transition.params,
        };
        state.slides[
          currentSlideId
        ].transition.localVersion = localSlideVersion();
        state.localSlidesVersion = localSlidesVersion();
        break;
      }

      case 'SYMBOL_EDITION_START': {
        addCurrentModeToHistory(state);
        state.mode = MODE_SYMBOL;
        state.symbol = {
          ...action.symbolData,
          content: migrateSlideContent(action.symbolContent),
          dimensions: action.symbolDimensions,
        };
        state.canvasDimensions = action.symbolDimensions;
        break;
      }

      case 'SYMBOL_RENAME': {
        return decorate({
          ...state,
          symbol: {
            ...state.symbol,
            name: action.name,
          },
        });
      }

      case 'SYMBOL_EDITION_DIMENSIONS_UPDATE': {
        state.symbol.dimensions = action.symbolDimensions;
        state.canvasDimensions = action.symbolDimensions;
        break;
      }

      case 'SYMBOL_EDITION_END': {
        const parentModes = state.parentModes.concat();
        const parentMode = parentModes.pop();
        state.parentModes = parentModes;
        state.mode = parentMode.mode;
        state.symbol = defaultState.symbol;
        state.canvasDimensions = documentSize;
        break;
      }

      case 'ADD_TEMPLATE': {
        const template = action.template;
        state.editingTheme.templates[template.id] = template;
        break;
      }

      case 'TEMPLATE_EDITION_START': {
        addCurrentModeToHistory(state);
        state.mode = MODE_TEMPLATE;
        state.currentTemplateId = action.template.id;
        let order =
          action.editingTheme.order ||
          sortBy(
            Object.values(action.editingTheme.templates)
              .map(t => t.id)
              .filter(Boolean),
            template => template.type || '',
          );
        order = order.filter(id => action.editingTheme.templates[id]);

        const templates = {};
        order.forEach(id => {
          if (action.editingTheme.templates[id]) {
            templates[id] = action.editingTheme.templates[id];
          }
        });
        if (!order.find(id => id === action.template.id)) {
          order.push(action.template.id);
        }

        state.editingTheme = {
          ...action.editingTheme,
          order,
          templates: { ...templates, [action.template.id]: action.template },
        };
        state.template = {
          ...defaultState.template,
          ...action.template,
        };
        state.canvasDimensions = {
          width: action.template.width,
          height: action.template.height,
        };
        break;
      }

      case 'TEMPLATE_EDITION_CANCEL': {
        const parentModes = state.parentModes.concat();
        const parentMode = parentModes.pop();
        state.parentModes = parentModes;
        state.mode = parentMode.mode;
        state.template = { ...defaultState.template };
        state.canvasDimensions = documentSize;
        break;
      }

      case 'TEMPLATE_EDITION_END': {
        const parentModes = state.parentModes.concat();
        const parentMode = parentModes.pop();
        updateSlidesTemplateData(state, action.data);
        state.parentModes = parentModes;
        state.mode = parentMode.mode;
        state.template = { ...defaultState.template };
        // state.editingTheme = null;
        state.canvasDimensions = documentSize;
        break;
      }

      case 'TEMPLATE_PENDING_UPDATE': {
        state.editingTheme.templates[state.currentTemplateId] = {
          ...state.editingTheme.templates[state.currentTemplateId],
          ...action.data,
        };
        state.template = {
          ...state.template,
          ...action.data,
        };
        break;
      }
      default:
        return state;
    }
  };

  return canvasReducer;
};

function applyCanvasInitialised(
  state,
  action,
  localSlideVersion,
  localSlidesVersion,
) {
  const slidesCollection = action.slides.map(slide => ({
    ...slide,
    localVersion: localSlideVersion(),
  }));
  let newCurrentSlideId;

  if (state.currentSlideId) {
    newCurrentSlideId = state.currentSlideId;
  } else {
    const currentSlide = slidesCollection[action.index];
    const firstSlide = slidesCollection[0];
    newCurrentSlideId = (currentSlide || firstSlide).id;
  }

  return decorate({
    ...state,
    currentSlideId: newCurrentSlideId,
    slides: keyBy(slidesCollection, 'id'),
    title: action.title,
    description: action.description,
    googleDriveId: action.googleDriveId,
    localSlidesVersion: localSlidesVersion(),
    order: action.order,
    scrollableViewer: action.scrollableViewer,
    hideControls: action.hideControls,
    updatedAt: action.updatedAt,
    publishedAt: action.publishedAt,
  });
}

function applyPresentationRefresh(
  state,
  action,
  localSlideVersion,
  localSlidesVersion,
) {
  if (action.slidesWithoutID) {
    // slides without ID results from updates on already deleted slide
    // (ie: change notes from a published presentation on aslide deleted in the draft version)
    action.slidesWithoutID.forEach(([id, content]) => {
      state.slides[id] = null;
    });
  }
  action.slidesModified.forEach(id => {
    const setSlide = action.setSlides.find(slide => slide.id === id) || {
      content: { objects: {} },
    };
    state.slides[id] = {
      ...setSlide,
      content: {
        ...(setSlide.content || {}),
        objects: { ...setSlide.content?.objects },
      },
      localVersion: localSlideVersion(),
    };
  });

  const order = state.order;
  if (action.setOrder) {
    if (action.setOrder.length === action.setSlides.length) {
      if (JSON.stringify(action.setOrder) !== JSON.stringify(order)) {
        state.order = action.setOrder;
      }
    } else {
      state.order = action.setOrder.filter(id =>
        action.setSlides.find(slide => slide.id === id),
      );
    }
  }

  if (state.currentSlideIndex < 0) {
    state.currentSlideIndex = 0;
  }
  state.title = action.setTitle;
  state.scrollableViewer = action.setScrollableViewer;
  state.hideControls = action.setHideControls;
  state.updatedAt = action.setUpdatedAt || state.updatedAt || 0;
  state.publishedAt = action.setLastPublished || state.publishedAt;
  state.description = action.setDescription || state.description;
  state.googleDriveId = action.setGoogleDriveId || state.googleDriveId;
  state.localSlidesVersion = localSlidesVersion();
}

function applyReplaceImage(state, action) {
  const { slides, currentSlideId } = state;

  const getNewObjects = (objects, targetId, overrides) => {
    const targetObject = objects[targetId];
    if (targetObject) {
      merge(
        targetObject,
        overrideImageData(targetObject, overrides, state.mode === MODE_SLIDE),
      );
    }
  };

  if (state.mode === MODE_SLIDE) {
    const currentSlide = slides[currentSlideId];
    if (action.overrides.editingSymbol) {
      const editingObjects = action.overrides.editingSymbol.editingObjects;
      const editingObjectsHashMap = keyBy(editingObjects, 'canvasId');
      getNewObjects(editingObjectsHashMap, action.targetId, action.overrides);
    } else {
      getNewObjects(
        currentSlide.content.objects,
        action.targetId,
        action.overrides,
      );
    }
  }
  if (state.mode === MODE_SYMBOL) {
    getNewObjects(
      state.symbol.content.objects,
      action.targetId,
      action.overrides,
    );
  }
  if (state.mode === MODE_TEMPLATE) {
    getNewObjects(
      state.template.content.objects,
      action.targetId,
      action.overrides,
    );
  }
}

function applyNewTemplate(draft, action) {
  const insertIndex = action.tail
    ? draft.editingTheme.order.length
    : draft.currentSlideIndex + 1; // TODO
  const newId = action.id;
  const beforeIds = draft.editingTheme.order.slice(0, insertIndex);
  const afterIds = draft.editingTheme.order.slice(insertIndex);
  const newOrder = [...beforeIds, newId, ...afterIds];
  const currentTemplate = getCurrentTemplate(draft);
  draft.editingTheme.templates[newId] = {
    ...currentTemplate,
    id: newId,
    type: 'custom',
    name: '',
    content: {
      ...currentTemplate.content,
      objects: {}, // TODO
    },
  };
  draft.currentTemplateId = newId;
  draft.editingTheme.order = newOrder;
  draft.currentSelection = [];
  mutableDecorate(draft);
}

function applyNewSlide(draft, action, localSlideVersion, localSlidesVersion) {
  const insertIndex = action.tail
    ? draft.order.length
    : draft.currentSlideIndex + 1;
  const newId = action.id;
  const beforeIds = draft.order.slice(0, insertIndex);
  const afterIds = draft.order.slice(insertIndex);
  const newOrder = [...beforeIds, newId, ...afterIds];
  if (draft.mode === MODE_TEMPLATE) {
    return true;
  }
  draft.slides[newId] = {
    id: newId,
    templateId: action.templateId,
    templateVersion: action.templateVersion,
    content: {
      background: action.background,
      objects: action.objects || {}, // XXX: Should we deepCopy here?
    },
    transition: action.transition,
    localVersion: localSlideVersion(),
  };
  draft.currentSlideId = newId;
  draft.order = newOrder;
  draft.localSlidesVersion = localSlidesVersion();
  draft.currentSelection = [];
  mutableDecorate(draft);
}

function applyPasteSlides(draft, action, localSlidesVersion) {
  const copiedSlidesIds = map(action.slides, 'id');
  const copiedSlidesMap = keyBy(action.slides, 'id');
  const beforeSlidesIds = draft.order.slice(0, action.index);
  const afterSlidesIds = draft.order.slice(action.index);

  const newOrder = [...beforeSlidesIds, ...copiedSlidesIds, ...afterSlidesIds];

  copiedSlidesIds.forEach(slideId => {
    draft.slides[slideId] = copiedSlidesMap[slideId];
  });
  draft.order = newOrder;
  draft.currentSlideId = action.newId;
  draft.localSlidesVersion = localSlidesVersion();
}

function applyPasteTemplates(draft, action, localSlidesVersion) {
  const copiedSlidesIds = map(action.slides, 'id');
  const copiedSlidesMap = keyBy(action.slides, 'id');
  const beforeSlidesIds = draft.editingTheme.order.slice(0, action.index);
  const afterSlidesIds = draft.editingTheme.order.slice(action.index);

  const newOrder = [...beforeSlidesIds, ...copiedSlidesIds, ...afterSlidesIds];
  const currentTemplate = getCurrentTemplate(draft);
  copiedSlidesIds.forEach(slideId => {
    draft.editingTheme.templates[slideId] = {
      ...currentTemplate,
      ...copiedSlidesMap[slideId],
      type: 'custom',
      name: '',
    };
  });
  draft.editingTheme.order = newOrder;
  draft.currentTemplateId = action.newId;
}

function applyDuplicateSlides(
  draft,
  action,
  localSlideVersion,
  localSlidesVersion,
) {
  const copiedSlides = action.idTuples.map(idTuple => {
    const copiedSlide = draft.slides[idTuple.from];
    return {
      ...copiedSlide,
      content: {
        ...clone(copiedSlide.content),
        objects: identifyDataObjectTree(cloneDeep(copiedSlide.content.objects)),
      },
      id: idTuple.to,
      localVersion: localSlideVersion(),
    };
  });
  const copiedSlidesIds = map(copiedSlides, 'id');
  const copiedSlidesMap = keyBy(copiedSlides, 'id');
  const beforeSlidesIds = draft.order.slice(0, action.index);
  const afterSlidesIds = draft.order.slice(action.index);
  const newOrder = [...beforeSlidesIds, ...copiedSlidesIds, ...afterSlidesIds];

  copiedSlidesIds.forEach(slideId => {
    draft.slides[slideId] = copiedSlidesMap[slideId];
  });
  draft.order = newOrder;
  draft.currentSlideId = action.newId;
  draft.localSlidesVersion = localSlidesVersion();
  draft.totalSlides = newOrder.length;
}

function applyDuplicateTemplates(draft, action) {
  const copiedSlides = action.idTuples.map(idTuple => {
    const copiedSlide = draft.editingTheme.templates[idTuple.from];
    return {
      ...copiedSlide,
      content: {
        ...clone(copiedSlide.content),
        objects: identifyDataObjectTree(cloneDeep(copiedSlide.content.objects)),
      },
      type: 'custom',
      name: '',
      id: idTuple.to,
    };
  });
  const copiedSlidesIds = map(copiedSlides, 'id');
  const copiedSlidesMap = keyBy(copiedSlides, 'id');
  const beforeSlidesIds = draft.editingTheme.order.slice(0, action.index);
  const afterSlidesIds = draft.editingTheme.order.slice(action.index);
  const newOrder = [...beforeSlidesIds, ...copiedSlidesIds, ...afterSlidesIds];

  copiedSlidesIds.forEach(slideId => {
    draft.editingTheme.templates[slideId] = copiedSlidesMap[slideId];
  });
  draft.editingTheme.order = newOrder;
  draft.currentTemplateId = action.newId;
}

function applyPreviousSlide(draft) {
  draft.currentSlideId = getIdByIndex(draft.order, draft.currentSlideIndex - 1);

  mutableDecorate(draft);
}

function applyNextSlide(draft) {
  draft.currentSlideId = getIdByIndex(draft.order, draft.currentSlideIndex + 1);

  mutableDecorate(draft);
}

function applyGoToSlide(draft, action) {
  if (draft.mode === MODE_TEMPLATE) {
    draft.currentTemplateId = draft.editingTheme.order
      ? getIdByIndex(draft.editingTheme.order, action.index)
      : Object.keys(draft.editingTheme.templates)[action.index];
    draft.template = draft.editingTheme.templates[draft.currentTemplateId];
  } else {
    draft.currentSlideId = getIdByIndex(draft.order, action.index);
  }
  mutableDecorate(draft);
}

function applyDeleteSlides(draft, action, localSlidesVersion) {
  if (draft.mode === MODE_TEMPLATE) {
    action.ids.forEach(id => {
      unset(draft.editingTheme.templates, id);
      draft.editingTheme.order.splice(indexOf(draft.editingTheme.order, id), 1);
      draft.currentTemplateId = action.newId;
    });
  } else {
    action.ids.forEach(id => {
      unset(draft.slides, id);
      draft.order.splice(indexOf(draft.order, id), 1);
    });

    draft.currentSlideId = action.newId;
    draft.localSlidesVersion = localSlidesVersion();
  }
  mutableDecorate(draft);
}

function applyReorder(draft, action, localSlidesVersion) {
  if (draft.mode !== MODE_TEMPLATE) {
    draft.currentSlideId = action.newId;
    draft.order = action.ids;
    draft.localSlideVersion = localSlidesVersion();
  } else {
    draft.editingTheme.order = action.ids;
  }

  mutableDecorate(draft);
}

function applySlidesSkipped(draft, action) {
  action.ids.forEach(id => {
    draft.slides[id].visible = false;
  });

  mutableDecorate(draft);
}

function applySlidesUnskipped(draft, action) {
  action.ids.forEach(id => {
    draft.slides[id].visible = true;
  });

  mutableDecorate(draft);
}
